import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { BadgeDialog, SetPinDialog } from '@/components/production/PeopleTab';
import {
  DocumentsPanel,
  GoalsPanel,
  PayPanel,
  ProfileFields,
  RoleFields,
  TimelinePanel,
  WorkModeCard,
} from '@/components/hr/EmployeePanels';
import { useProfiles } from '@/features/users/hooks';
import { EmployeeActivityPanel } from '@/components/users/EmployeeActivityPanel';
import { useAuth } from '@/app/AuthProvider';
import {
  useAllHrInfo,
  useEmployeePrivateNotes,
  useSaveEmployee,
  type EmployeeDraft,
  type EmployeeHrInfo,
} from '@/features/hr/hooks';
import { friendlyHrError } from '@/features/hr/model';
import { canRead, ROLE_LABELS } from '@/lib/permissions';
import { useCredentialStatus } from '@/features/workMode/hooks';
import { useWorkLookups } from '@/features/workMode/useLookups';
import type { WorkPerson } from '@/features/workMode/useLookups';
import type { Tables } from '@/types/database';

const TABS = ['profile', 'role', 'pay', 'goals', 'documents', 'timeline', 'activity'] as const;
type TabId = (typeof TABS)[number];

/**
 * One employee's record. Profile fields that Work Mode already shows
 * (name, title, portrait) are edited here and written back onto the
 * profile. The PIN is the Work Mode credential, set through the same
 * hashed path the floor uses.
 */
export function EmployeeDetailPage() {
  const { profileId } = useParams<{ profileId: string }>();
  const [params, setParams] = useSearchParams();
  const { role } = useAuth();
  // The audit trail includes before/after snapshots of financial rows, so
  // the Activity tab follows the same gate as the Audit log page itself.
  const canSeeActivity = canRead(role, 'audit');
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const hrQuery = useAllHrInfo();
  const hrRows = hrQuery.data;
  const hrLoading = hrQuery.isLoading;
  const hrFetched = hrQuery.isFetched;
  const notes = useEmployeePrivateNotes(profileId ?? null);
  const lookups = useWorkLookups();
  const { data: credentials } = useCredentialStatus();
  const save = useSaveEmployee();
  const [pinOpen, setPinOpen] = useState(false);
  const [badgeOpen, setBadgeOpen] = useState(false);
  const [draft, setDraft] = useState<EmployeeDraft | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  const profile = (profiles ?? []).find((p) => p.id === profileId) ?? null;
  const hr = (hrRows ?? []).find((row) => row.profile_id === profileId) ?? null;
  const ready = !!profile && hrFetched && !hrQuery.isError && notes.isSuccess;
  const seededDraft = profile && ready ? toDraft(profile, hr, notes.data?.hr_notes ?? '') : null;

  if (profile && seededDraft && seededFor !== profile.id) {
    setSeededFor(profile.id);
    setDraft(seededDraft);
  }

  const liveDraft = profile && seededFor === profile.id && draft ? draft : seededDraft;

  const tabParam = params.get('tab');
  const requestedTab: TabId = TABS.includes(tabParam as TabId) ? (tabParam as TabId) : 'profile';
  const tab: TabId = requestedTab === 'activity' && !canSeeActivity ? 'profile' : requestedTab;
  const workPerson: WorkPerson | null = profile ? lookups.person(profile.id) : null;
  const managers = lookups.peopleList
    .filter((p) => p.isActive && p.id !== profile?.id && p.role !== 'external_lender')
    .map((p) => ({ id: p.id, name: p.name }));
  if (liveDraft?.manager_id && !managers.some((m) => m.id === liveDraft.manager_id)) {
    const manager = (profiles ?? []).find((p) => p.id === liveDraft.manager_id);
    if (manager) managers.unshift({ id: manager.id, name: manager.full_name || manager.email });
  }

  const onSave = async () => {
    if (!profile || !liveDraft) return;
    const draft = liveDraft;
    try {
      await save.mutateAsync({
        profileId: profile.id,
        profile: {
          full_name: draft.full_name,
          job_title: draft.job_title,
          start_date: draft.start_date,
          date_of_birth: draft.date_of_birth,
          phone: draft.phone,
          personal_email: draft.personal_email,
          pronouns: draft.pronouns,
          bio: draft.bio,
          timezone: draft.timezone,
          address_line1: draft.address_line1,
          address_line2: draft.address_line2,
          city: draft.city,
          state: draft.state,
          postal_code: draft.postal_code,
          country: draft.country,
          emergency_contact_name: draft.emergency_contact_name,
          emergency_contact_phone: draft.emergency_contact_phone,
          emergency_contact_relationship: draft.emergency_contact_relationship,
        },
        hr: {
          preferred_name: draft.preferred_name,
          position: draft.position,
          department: draft.department,
          employment_type: draft.employment_type,
          employment_status: draft.employment_status,
          manager_id: draft.manager_id,
          work_location: draft.work_location,
          shirt_size: draft.shirt_size,
          allergies: draft.allergies,
          dietary_notes: draft.dietary_notes,
          favorite_snack: draft.favorite_snack,
          coffee_order: draft.coffee_order,
          languages: draft.languages,
          interests: draft.interests,
          celebration_notes: draft.celebration_notes,
          celebrate_birthday: draft.celebrate_birthday,
          secondary_emergency_name: draft.secondary_emergency_name,
          secondary_emergency_phone: draft.secondary_emergency_phone,
          secondary_emergency_relationship: draft.secondary_emergency_relationship,
          hr_notes: draft.hr_notes,
        },
      });
      toast.success('Saved.');
    } catch (err) {
      toast.error(friendlyHrError(err));
    }
  };

  if (profilesLoading || hrLoading || (!!profileId && !notes.isFetched)) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (hrQuery.isError || notes.isError) {
    return (
      <EmptyState
        icon={Users}
        title="Could not open this record"
        detail={
          (hrQuery.error instanceof Error && hrQuery.error.message) ||
          (notes.error instanceof Error && notes.error.message) ||
          'The employee record did not load. Apply the latest database migration and try again.'
        }
      />
    );
  }

  if (!profile || !liveDraft || !workPerson) {
    return (
      <EmptyState
        icon={Users}
        title="Person not found"
        action={
          <Button asChild variant="outline">
            <Link to="/hr/employees">Back to employees</Link>
          </Button>
        }
      />
    );
  }

  const displayName = liveDraft.preferred_name.trim() || profile.full_name || profile.email;
  const showSave = tab === 'profile' || tab === 'role';
  const cred = credentials?.get(profile.id);

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/hr/employees">
            <ArrowLeft className="h-3.5 w-3.5" />
            Employees
          </Link>
        </Button>
        <PageHeader
          eyebrow="HR"
          title={displayName}
          meta={
            <span className="flex flex-wrap items-center gap-2">
              <span>{profile.email}</span>
              <Badge variant="info-soft">{ROLE_LABELS[profile.role]}</Badge>
              {!profile.is_active && <Badge variant="muted">Inactive account</Badge>}
              {liveDraft.job_title && <span>{liveDraft.job_title}</span>}
            </span>
          }
          actions={
            showSave ? (
              <Button type="button" onClick={onSave} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save record'}
              </Button>
            ) : null
          }
        />
      </div>

      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          {profile.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
          <AvatarFallback>{initials(displayName, profile.email)}</AvatarFallback>
        </Avatar>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) => {
          const next = new URLSearchParams(params);
          next.set('tab', value);
          setParams(next, { replace: true });
        }}
      >
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="role">Role & PIN</TabsTrigger>
          <TabsTrigger value="pay">Pay</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          {canSeeActivity && <TabsTrigger value="activity">Activity</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile">
          <ProfileFields draft={liveDraft} onChange={setDraft} />
        </TabsContent>
        <TabsContent value="role" className="space-y-4">
          <RoleFields draft={liveDraft} onChange={setDraft} managers={managers} />
          <WorkModeCard
            person={workPerson}
            credential={cred}
            onSetPin={() => setPinOpen(true)}
            onSetBadge={() => setBadgeOpen(true)}
          />
        </TabsContent>
        <TabsContent value="pay">
          <PayPanel
            profileId={profile.id}
            payRateCents={hr?.pay_rate_cents ?? null}
            payType={hr?.pay_type ?? 'hourly'}
          />
        </TabsContent>
        <TabsContent value="goals">
          <GoalsPanel profileId={profile.id} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentsPanel profileId={profile.id} />
        </TabsContent>
        <TabsContent value="timeline">
          <TimelinePanel profileId={profile.id} />
        </TabsContent>
        {canSeeActivity && (
          <TabsContent value="activity">
            <EmployeeActivityPanel profileId={profile.id} displayName={displayName} />
          </TabsContent>
        )}
      </Tabs>

      <SetPinDialog person={pinOpen ? workPerson : null} onOpenChange={setPinOpen} />
      <BadgeDialog
        person={badgeOpen ? workPerson : null}
        hasBadge={!!cred?.has_badge}
        onOpenChange={setBadgeOpen}
      />
    </div>
  );
}

function toDraft(
  profile: Tables<'profiles'>,
  hr: EmployeeHrInfo | null,
  hrNotes: string,
): EmployeeDraft {
  return {
    full_name: profile.full_name ?? '',
    job_title: profile.job_title ?? '',
    start_date: (hr?.start_date ?? profile.start_date ?? '').slice(0, 10),
    date_of_birth: (profile.date_of_birth ?? '').slice(0, 10),
    phone: profile.phone ?? '',
    personal_email: profile.personal_email ?? '',
    pronouns: profile.pronouns ?? '',
    bio: profile.bio ?? '',
    timezone: profile.timezone ?? '',
    address_line1: profile.address_line1 ?? '',
    address_line2: profile.address_line2 ?? '',
    city: profile.city ?? '',
    state: profile.state ?? '',
    postal_code: profile.postal_code ?? '',
    country: profile.country ?? '',
    emergency_contact_name: profile.emergency_contact_name ?? '',
    emergency_contact_phone: profile.emergency_contact_phone ?? '',
    emergency_contact_relationship: profile.emergency_contact_relationship ?? '',
    preferred_name: hr?.preferred_name ?? '',
    position: hr?.position ?? '',
    department: hr?.department ?? '',
    employment_type: hr?.employment_type ?? '',
    employment_status: hr?.employment_status ?? 'active',
    manager_id: hr?.manager_id ?? '',
    work_location: hr?.work_location ?? '',
    shirt_size: hr?.shirt_size ?? '',
    allergies: hr?.allergies ?? '',
    dietary_notes: hr?.dietary_notes ?? '',
    favorite_snack: hr?.favorite_snack ?? '',
    coffee_order: hr?.coffee_order ?? '',
    languages: hr?.languages ?? '',
    interests: hr?.interests ?? '',
    celebration_notes: hr?.celebration_notes ?? '',
    celebrate_birthday: hr?.celebrate_birthday ?? true,
    secondary_emergency_name: hr?.secondary_emergency_name ?? '',
    secondary_emergency_phone: hr?.secondary_emergency_phone ?? '',
    secondary_emergency_relationship: hr?.secondary_emergency_relationship ?? '',
    hr_notes: hrNotes,
  };
}

function initials(name: string, email: string): string {
  return (name || email)
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
}
